import { getDateDisplay, getTimeDisplay } from 'src/utils/momentUtils';

export type Status = 'active' | 'completed' | 'pending' | 'rejected';
export type FileType = 'exhibit' | 'final' | 'production';
export type FileData = {
  depositionId: string;
  uploaderUserId: string;
  name: string;
  extension: string;
  uri: string;
  id: string;
  isUseInFinalTranscript?: boolean;
  isSelected?: boolean;
  order: number;
  updatedDate: string;
  uploadUrl: string;
  isRejected?: boolean;
  rejectionReason?: string;
  downloadUri: string;
  isPublic?: boolean;
  size?: number;
};

export type People = {
  id: string;
  name: string;
  email: string;
  role: string;
  position: string;
  imageURL: string;
  firstName?: string;
  lastName?: string;
  isSelected?: boolean;
  canAccessFinalDeposition?: boolean;
  companyOrLawFirm?: string;
};

export type Deposition = {
  id: string;
  caseId: string;
  caseName: string;
  name: string;
  status: DepositionStatus;
  caption: string;
  actualDepositionDate: string;
  actualDepositionTime: string;
  depositionDate: string;
  openDate: string;
  circuitId: number;
  rejectionReason?: string;
  isPaidInFull?: boolean;
  involvedParties: People[];
  requester?: People;
  isLocked?: boolean;
  uri: string;
  files: {
    exhibit: FileData[];
    final: FileData[];
  };
  subResources: {
    exhibitUploaders: {
      uri: string;
      total: number;
      uploadUri: string;
    };
    finalDepositionFiles: {
      uri: string;
      total: number;
      uploadUri: string;
    };
    productionFiles: {
      uri: string;
      total: number;
      uploadUri: string;
    };
    involvedPartiesUri: string;
    requesterUserUri: string;
  };
};

export type DepositionStatus = 'active' | 'completed' | 'pending' | 'rejected';

/*----------------- Get deposition list --------------------------*/
export type IDepositionListItem = {
  id: string;
  caseId: string;
  name: string;
  status: DepositionStatus;
  openDate: string;
  isLocked: boolean;
};

export type IDepositionListItemFile = {
  id: string;
  name: string;
  extension: string;
  mimeType: string;
  order: number;
  useInFinal: boolean;
  depositionId: string;
  uploaderUserId: string;
  resourceUrl: string;
};

/*----------------- End get deposition list --------------------------*/

export type DepositionFiles = {
  exhibit: FileData[];
  final: FileData[];
  production: FileData[];
};

export const rawDataToDepositionListItem = (depos: any[]) => {
  return depos.map(depo => ({
    id: depo.id,
    caseId: depo.caseId,
    caseName: depo.caseName,
    name: depo.name,
    status: depo.status,
    openDate: getDateDisplay(depo.openDate),
    actualDate: getDateDisplay(depo.actualDate),
    isLocked: depo.isLocked ?? false,
    subResources: {
      circuitUri: depo.subResources.circuitUri,
      exhibitUploaderUri: depo.subResources.exhibitUploaders.uri,
      productionFilesUri: depo.subResources.productionFiles?.uri,
      finalDepositionFilesUri: depo.subResources.finalDepositionFiles.uri,
      involvedPartiesUri: depo.subResources.involvedPartiesUri,
      requesterUserUri: depo.subResources.requesterUserUri,
    },
  }));
};

const responseStatusToDepositionStatus = (depositionStatus: string) => {
  return depositionStatus.toLocaleLowerCase() as DepositionStatus;
};

export const getUserName = (user: any) => {
  if (!user.firstName && !user.lastName) {
    return user.email.split('@')[0];
  }
  return `${user.firstName} ${user.lastName}`;
};

/**
 * Create InvolvedPaties from InvolvedPatiesReponse
 * @param {InvolvedPatiesReponse} response
 */
const patiesResponseToPeople = (people: any) => {
  return {
    id: people.id,
    depositionId: people.depositionId,
    name: getUserName(people),
    email: people.email,
    role: people.role,
    position: people.role,
    imageURL: people.imageURL ?? 'https://picsum.photos/200/300',
    canAccessFinalDeposition: people.canAccessFinalDeposition ?? false,
  } as People;
};
/**
 * Create InvolvedPaties from InvolvedPatiesReponse
 * @param {InvolvedPatiesReponse} response
 */

const involvedPatiesResponseToPeople = (peoples: any[]) => {
  try {
    return peoples.map(
      people =>
        ({
          id: people.id,
          depositionId: people.depositionId,
          name: getUserName(people),
          email: people.email,
          role: people.role,
          position: people.role,
          imageURL: people.imageURL ?? 'https://picsum.photos/200/300',
          canAccessFinalDeposition: people.canAccessFinalDeposition ?? false,
        } as People),
    );
  } catch (error) {
    return [];
  }
};
/**
 * Create DataFile from DataFileReponse
 * @param {DataFileReponse} response
 */
const dataFileResponseToDataFile = (file: any) => {
  return {
    depositionId: file.depositionId,
    uploaderUserId: file.uploaderUserId,
    name: file.name,
    id: file.id,
    uri: file.uri,
    extension: file.extension,
    order: file.order,
    isUseInFinalTranscript: file.useInFinal,
    resourceUrl: file.resourceUrl,
    isSelected: false,
    rejectionReason: file.rejectionReason,
    isRejected: file.submissionStatus ? file.submissionStatus === 'REJECTED' : false,
    // isRejected: true,
    isPublic: file.submissionStatus ? file.submissionStatus === 'PUBLIC' : false,
    updatedDate: file.updatedDate,
    downloadUri: file.downloadUri,
    size: file.size,
  };
};

/**
 * Deposition Response to Deposition
 * @param {DepositionResponse} response
 */
const depositionResponseToDeposition = (deposition: any) => {
  try {
    return {
      // metadata
      id: deposition.id,
      caseId: deposition.caseId,
      caseName: deposition.caseName,
      name: deposition.name,
      rejectionReason: deposition.rejectionReason,
      status: responseStatusToDepositionStatus(deposition.status),
      caption: deposition.caption,
      actualDepositionDate: getDateDisplay(deposition.actualDate),
      actualDepositionTime: getTimeDisplay(deposition.actualDate),
      depositionDate: deposition.actualDate,
      openDate: getDateDisplay(deposition.openDate),
      circuitId: deposition.circuitId,
      involvedParties: [],
      files: {
        exhibit: [],
        final: [],
        production: [],
      },
      isLocked: deposition.isLocked ?? false,
      subResources: {
        exhibitUploaders: deposition.subResources.exhibitUploaders,
        finalDepositionFiles: deposition.subResources.finalDepositionFiles,
        productionFiles: deposition.subResources.productionFiles,
        involvedPartiesUri: deposition.subResources.involvedPartiesUri,
        requesterUserUri: deposition.subResources.requesterUserUri,
      },
      uri: deposition.uri,
    } as Deposition;
  } catch (er) {
    console.error('TripFactory', er);
  }
  return {};
};

/**
 * ExhibitFile Response to ExhibitFiles
 * @param {ExhibitFiles} response
 */
const exhibitFileResponseToDataFiles = (response: any) => {
  try {
    const { exhibitUploaders } = response;

    return exhibitUploaders.map((file: any) => dataFileResponseToDataFile(file));
  } catch (er) {
    console.error('ExhibitFile Factory', er);
  }
  return [];
};

/**
 * ExhibitFile Response to ExhibitFiles
 * @param {ExhibitFiles} response
 */
const finalFileResponseToDataFiles = (response: any) => {
  try {
    const { finalDepositionFiles } = response;

    return finalDepositionFiles.map((file: any) => dataFileResponseToDataFile(file));
  } catch (er) {
    console.error('FinalFile Factory', er);
  }
  return [];
};

/**
 * ExhibitFile Response to ExhibitFiles
 * @param {ExhibitFiles} response
 */
const productionFileResponseToDataFiles = (response: any) => {
  try {
    const { productionFiles } = response;
    return productionFiles.map((file: any) => dataFileResponseToDataFile(file));
  } catch (er) {
    console.error('productionFiles Factory', er);
  }
  return [];
};

export const dummyFiles: FileData[] = [
  {
    depositionId: '1',
    order: 1,
    name: 'Document 1',
    extension: 'pdf',
    uri: '',
    uploadUrl: '',
    id: '1',
    downloadUri: '',
    uploaderUserId: '1',
    updatedDate: '2021-05-27T05:16:04.934Z',
  },
];

export const dummyFinalFiles: FileData[] = [
  {
    depositionId: '1',
    uri: '',
    order: 1,
    name: 'Document 4',
    extension: 'pptx',
    uploadUrl: '',
    id: '4',
    downloadUri: '',
    uploaderUserId: '1',
    isPublic: true,
    updatedDate: '2021-05-27T05:16:04.934Z',
  },
  {
    depositionId: '1',
    uri: '',
    order: 2,
    name: 'Document 5',
    downloadUri: '',
    extension: 'pptx',
    uploadUrl: '',
    id: '5',
    uploaderUserId: '2',
    isPublic: false,
    updatedDate: '2021-05-27T05:16:04.934Z',
  },
];

export const dummyDepotionsData: Array<Deposition> = [
  {
    id: '1',
    uri: '1',
    name: 'Deposition 1',
    caseName: 'Case 1',
    status: 'completed' as Status,
    circuitId: 1,
    caption:
      'Pariatur occaecat aliqua pariatur id cillum laborum consequat esse. Reprehenderit proident eiusmod tempor reprehenderit in laborum anim.',
    caseId: '4444-55',
    openDate: '02/22/2022',
    actualDepositionDate: '02/23/2022',
    actualDepositionTime: '08:30',
    depositionDate: '2021-05-13T21:00:25.088Z',
    involvedParties: [
      {
        id: '1',
        name: 'Esther Howard',
        role: 'Court Reporter',
        position: 'Court Reporter',
        email: 'example @email.com',
        imageURL: 'https://picsum.photos/200/300',
      },
      {
        id: '2',
        name: 'Cameron Williamson',
        role: 'Court Reporter',
        position: 'Court Reporter',
        email: 'example @email.com',
        imageURL: 'https://picsum.photos/200/300',
      },
    ],
    requester: {
      id: '2',
      name: 'Nga',
      role: 'Court Reporter',
      position: 'Court Reporter',
      email: 'example @email.com',
      imageURL: 'https://picsum.photos/200/300',
    },
    isLocked: true,
    files: {
      exhibit: dummyFiles,
      final: dummyFinalFiles,
    },
    subResources: {
      exhibitUploaders: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      finalDepositionFiles: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      productionFiles: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      involvedPartiesUri: '',
      requesterUserUri: '',
    },
  },
  {
    id: '2',
    uri: '1',
    status: 'active' as Status,
    name: 'Deposition 2',
    caseName: 'Case 2',
    circuitId: 1,
    caption:
      'Pariatur occaecat aliqua pariatur id cillum laborum consequat esse. Reprehenderit proident eiusmod tempor reprehenderit in laborum anim.',
    caseId: '4444-55',
    openDate: '02/22/2022',
    depositionDate: '2021-05-13T21:00:25.088Z',
    actualDepositionDate: '02/23/2022',
    actualDepositionTime: '08:30',
    involvedParties: [
      {
        id: '1',
        name: 'Esther Howard',
        role: 'Court Reporter',
        position: 'Court Reporter',
        email: 'example @email.com',
        imageURL: 'https://picsum.photos/200/300',
      },
      {
        id: '2',
        name: 'Cameron Williamson',
        role: 'Court Reporter',
        position: 'Court Reporter',
        email: 'example @email.com',
        imageURL: 'https://picsum.photos/200/300',
      },
    ],
    files: {
      exhibit: dummyFiles,
      final: dummyFinalFiles,
    },
    subResources: {
      exhibitUploaders: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      finalDepositionFiles: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      productionFiles: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      involvedPartiesUri: '',
      requesterUserUri: '',
    },
  },
  {
    id: '3',
    uri: '1',
    status: 'pending' as Status,
    name: 'Deposition 3',
    caseName: 'Case 3',
    circuitId: 1,
    caption:
      'Pariatur occaecat aliqua pariatur id cillum laborum consequat esse. Reprehenderit proident eiusmod tempor reprehenderit in laborum anim.',
    caseId: '4444-55',
    openDate: '02/22/2022',
    actualDepositionDate: '02/23/2022',
    actualDepositionTime: '08:30',
    depositionDate: '2021-05-13T21:00:25.088Z',
    involvedParties: [
      {
        id: '1',
        name: 'Esther Howard',
        role: 'Court Reporter',
        position: 'Court Reporter',
        email: 'example @email.com',
        imageURL: 'https://picsum.photos/200/300',
      },
      {
        id: '2',
        name: 'Cameron Williamson',
        role: 'Court Reporter',
        position: 'Court Reporter',
        email: 'example @email.com',
        imageURL: 'https://picsum.photos/200/300',
      },
    ],
    files: {
      exhibit: dummyFiles,
      final: dummyFinalFiles,
    },
    subResources: {
      exhibitUploaders: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      finalDepositionFiles: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      productionFiles: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      involvedPartiesUri: '',
      requesterUserUri: '',
    },
  },
  {
    id: '4',
    uri: '1',
    status: 'rejected' as Status,
    name: 'Deposition 4',
    caseName: 'Case 4',
    circuitId: 1,
    caption:
      'Pariatur occaecat aliqua pariatur id cillum laborum consequat esse. Reprehenderit proident eiusmod tempor reprehenderit in laborum anim.',
    caseId: '4444-55',
    openDate: '02/22/2022',
    actualDepositionDate: '02/23/2022',
    actualDepositionTime: '08:30',
    depositionDate: '2021-05-13T21:00:25.088Z',
    involvedParties: [
      {
        id: '1',
        name: 'Esther Howard',
        role: 'Court Reporter',
        position: 'Court Reporter',
        email: 'example @email.com',
        imageURL: 'https://picsum.photos/200/300',
      },
      {
        id: '2',
        name: 'Cameron Williamson',
        role: 'Court Reporter',
        position: 'Court Reporter',
        email: 'example @email.com',
        imageURL: 'https://picsum.photos/200/300',
      },
    ],
    files: {
      exhibit: dummyFiles,
      final: dummyFinalFiles,
    },
    subResources: {
      exhibitUploaders: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      finalDepositionFiles: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      productionFiles: {
        uri: '',
        uploadUri: '',
        total: 1,
      },
      involvedPartiesUri: '',
      requesterUserUri: '',
    },
  },
];

export default {
  depositionResponseToDeposition,
  patiesResponseToPeople,
  involvedPatiesResponseToPeople,
  getUserName,
  exhibitFileResponseToDataFiles,
  finalFileResponseToDataFiles,
  productionFileResponseToDataFiles,
};
